import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getInvestorApiBase } from "../../utils/investorApiBase";
import { extractApiMessage } from "../../utils/extractApiMessage";
import {
  successAlert,
  errorAlert,
  warningAlert,
} from "../Reducers/appSlice";
import { authStorageGet } from "../../utils/authStorage";

/** Must match createApi({ reducerPath }) below — used to read cache in baseQuery */
const AUTH_API_REDUCER_PATH = "authApi";

const rawBaseQuery = fetchBaseQuery({
  baseUrl: getInvestorApiBase(),
  // fetch's response.ok is only true for 200–299; 304 Not Modified is 3xx, so without
  // this RTK Query treats 304 as an error → error toasts ("Something went wrong").
  validateStatus: (response) =>
    (response.status >= 200 && response.status <= 299) || response.status === 304,
  prepareHeaders: (headers) => {
    const token = authStorageGet("token");
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

function apply304NotModifiedPreserve(result, api) {
  if (
    result.error ||
    result.meta?.response?.status !== 304 ||
    !api.queryCacheKey
  ) {
    return result;
  }
  const data = result.data;
  const isEmpty =
    data == null ||
    data === "" ||
    (typeof data === "object" &&
      !Array.isArray(data) &&
      Object.keys(data).length === 0);
  if (!isEmpty) return result;
  const state = api.getState();
  const entry = state[AUTH_API_REDUCER_PATH]?.queries?.[api.queryCacheKey];
  const previousData = entry?.data;
  if (previousData === undefined) return result;
  return { ...result, data: previousData };
}

/**
 * Dispatches FloatingAlerts for API payloads.
 * - HTTP/network errors → error toast
 * - JSON with success: false + message → error toast
 * - JSON with message → success (mutations) or warning when severity/warning hints
 * - GET success toasts: opt-in with endpoint extraOptions: { toastQuerySuccess: true }
 * - Disable all toasts for an endpoint: extraOptions: { silentToast: true }
 */
async function baseQueryWithAlerts(args, api, extraOptions) {
  let result = apply304NotModifiedPreserve(
    await rawBaseQuery(args, api, extraOptions),
    api
  );

  if (extraOptions?.silentToast) return result;

  if (result.error) {
    api.dispatch(errorAlert({ message: extractApiMessage(result.error) }));
    return result;
  }

  const data = result.data;
  if (!data || typeof data !== "object") return result;

  const msg =
    data.message != null && String(data.message).trim()
      ? String(data.message)
      : "";

  if (data.success === false && msg) {
    api.dispatch(errorAlert({ message: msg }));
    return result;
  }

  if (!msg) return result;

  const method =
    typeof args === "object" && args.method != null
      ? String(args.method).toUpperCase()
      : "GET";
  const isMutation = ["POST", "PUT", "PATCH", "DELETE"].includes(method);
  const showForQuery = extraOptions?.toastQuerySuccess === true;

  if (!isMutation && !showForQuery) return result;

  const isWarning =
    data.warning === true ||
    String(data.severity || data.alertSeverity || "").toLowerCase() === "warning";

  if (isWarning) {
    api.dispatch(warningAlert({ message: msg }));
    return result;
  }

  api.dispatch(successAlert({ message: msg }));
  return result;
}

/** Admin get-investors / get-owners: only include optional filters when the user set them */
function buildAdminUserListQueryParams(params = {}) {
  const page = Number(params.page) > 0 ? Number(params.page) : 1;
  const limit = Number(params.limit) > 0 ? Number(params.limit) : 10;
  const out = { page, limit };
  const search =
    typeof params.search === "string" ? params.search.trim() : "";
  if (search) out.search = search;
  const kyc = params.kycStatus;
  if (
    kyc != null &&
    String(kyc).trim() !== "" &&
    String(kyc).trim().toLowerCase() !== "all"
  ) {
    out.kycStatus = String(kyc).trim();
  }
  const st = params.status;
  if (
    st != null &&
    String(st).trim() !== "" &&
    String(st).trim().toLowerCase() !== "all"
  ) {
    out.status = String(st).trim();
  }
  return out;
}

export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery: baseQueryWithAlerts,
  tagTypes: [
    "AdminAssets",
    "AdminAsset",
    "VerificationDetail",
    "VerificationList",
    "OwnerListings",
    "OwnerAssetDetail",
    "OwnerStakingPools",
    "AdminStakingPools",
    "InvestorStakingPools",
    "InvestorStakingPositions",
    "InvestorLoans",
    "AdminLoans",
  ],
  endpoints: (builder) => ({
    Login: builder.mutation({
      query: (credentials) => ({
        url: "/api/auth/login",
        method: "POST",
        body: credentials,
      }),
    }),
    signup: builder.mutation({
      query: (userData) => ({
        url: "/api/auth/register",
        method: "POST",
        body: userData,
      }),
    }),
    getAdminSettings: builder.query({
      query: () => ({
        url: "/api/admin/get-platform-settings",
        method: "GET",
      }),
    }),
    getAdminLogs: builder.query({
      query: () => ({
        url: "/api/admin/get-logs",
        method: "GET",
      }),
    }),
    getAdminDashboard: builder.query({
      query: () => ({
        url: "/api/admin/dashboard",
        method: "GET",
      }),
    }),
    getAdminAssets: builder.query({
      query: (params = {}) => ({
        url: "/api/admin/assets",
        method: "GET",
        params: {
          search: params.search ?? "",
          category: params.category ?? "",
          status: params.status ?? "",
          page: params.page ?? 1,
          limit: params.limit ?? 10,
        },
      }),
      providesTags: [{ type: "AdminAssets", id: "LIST" }],
    }),
    getAdminAssetDetail: builder.query({
      query: (assetId) => ({
        url: `/api/admin/assetDetail/${encodeURIComponent(String(assetId ?? "").trim())}`,
        method: "GET",
      }),
      providesTags: (result, error, assetId) => {
        const id = String(assetId ?? "").trim();
        return id ? [{ type: "AdminAsset", id }] : [];
      },
    }),
    getInvestorAssets: builder.query({
      query: (params = {}) => {
        const q = {
          search: params.search ?? "",
          status: params.status ?? "",
          page: params.page ?? 1,
          limit: params.limit ?? 10,
        };
        if (params.category != null && String(params.category).trim() !== "") {
          q.category = String(params.category).trim();
        }
        return {
          url: "/api/investor/assets",
          method: "GET",
          params: q,
        };
      },
    }),
    getInvestorAssetById: builder.query({
      query: (assetId) => ({
        url: `/api/investor/assets/${encodeURIComponent(String(assetId ?? "").trim())}`,
        method: "GET",
      }),
    }),
    getOwnerAssetById: builder.query({
      query: (assetId) => ({
        url: `/api/owner/asset/${encodeURIComponent(String(assetId ?? "").trim())}`,
        method: "GET",
      }),
      providesTags: (result, error, assetId) => [
        { type: "OwnerAssetDetail", id: String(assetId ?? "").trim() || "UNKNOWN" },
      ],
    }),
    getOwnerAssetInvestors: builder.query({
      query: ({ assetId, page = 1, limit = 10 }) => ({
        url: `/api/owner/asset/${encodeURIComponent(String(assetId ?? "").trim())}/investors`,
        method: "GET",
        params: {
          page: Number(page) || 1,
          limit: Number(limit) || 10,
        },
      }),
    }),
    getInvestorAssetSummary: builder.query({
      query: (assetId) => ({
        url: `/api/investor/assets/${encodeURIComponent(String(assetId ?? "").trim())}/summary`,
        method: "GET",
      }),
    }),
    getMyInvestments: builder.query({
      query: (params = {}) => ({
        url: "/api/investor/my-investments",
        method: "GET",
        params: {
          page: params.page ?? 1,
          limit: params.limit ?? 10,
        },
      }),
    }),
    /** GET /api/admin/loans — loan applications across all investors, by tab */
    getAdminLoans: builder.query({
      query: (params = {}) => ({
        url: "/api/admin/loans",
        method: "GET",
        params: {
          page: params.page ?? 1,
          limit: params.limit ?? 20,
          tab: params.tab ?? "all",
        },
      }),
      providesTags: [{ type: "AdminLoans", id: "LIST" }],
    }),
    /**
     * POST /api/admin/loans/:loanId/review — approve or reject a loan application.
     * `rejectionReason` is required when `action` is `reject`.
     */
    reviewAdminLoan: builder.mutation({
      query: ({ loanId, action, rejectionReason }) => ({
        url: `/api/admin/loans/${String(loanId ?? "").trim()}/review`,
        method: "POST",
        body: {
          action: String(action ?? "").trim(),
          ...(String(action ?? "").trim() === "reject"
            ? { rejectionReason: String(rejectionReason ?? "").trim() }
            : {}),
        },
      }),
      invalidatesTags: [
        { type: "AdminLoans", id: "LIST" },
        { type: "InvestorLoans", id: "LIST" },
      ],
    }),
    /**
     * POST /api/admin/loans/:loanId/sign-off — administrative sign-off.
     * `signatureData` is raw base64 PNG; `notes` is optional and may be empty.
     */
    signOffAdminLoan: builder.mutation({
      query: ({ loanId, signatureData, notes }) => ({
        url: `/api/admin/loans/${String(loanId ?? "").trim()}/sign-off`,
        method: "POST",
        body: {
          signatureData: String(signatureData ?? ""),
          notes: String(notes ?? ""),
        },
      }),
      invalidatesTags: [
        { type: "AdminLoans", id: "LIST" },
        { type: "InvestorLoans", id: "LIST" },
      ],
    }),
    /** GET /api/investor/loans — the current investor's loan applications, by tab */
    getInvestorLoans: builder.query({
      query: (params = {}) => ({
        url: "/api/investor/loans",
        method: "GET",
        params: {
          page: params.page ?? 1,
          limit: params.limit ?? 20,
          tab: params.tab ?? "all",
        },
      }),
      providesTags: [{ type: "InvestorLoans", id: "LIST" }],
    }),
    /**
     * GET /api/investor/loans/:assetId/collateral-preview — collateral value,
     * max loan amount and LTV for one of the investor's holdings.
     */
    getLoanCollateralPreview: builder.query({
      query: (assetId) => ({
        url: `/api/investor/loans/${String(assetId ?? "").trim()}/collateral-preview`,
        method: "GET",
      }),
    }),
    /** POST /api/investor/loans/:loanId/initiate-sign — start the signing session */
    initiateLoanSign: builder.mutation({
      query: (loanId) => ({
        url: `/api/investor/loans/${String(loanId ?? "").trim()}/initiate-sign`,
        method: "POST",
      }),
    }),
    /**
     * POST /api/investor/loans/:loanId/confirm-sign — submit the drawn signature.
     * `signatureData` is raw base64 PNG (no data-URI prefix).
     */
    confirmLoanSign: builder.mutation({
      query: ({ loanId, envelopeId = null, signatureData }) => ({
        url: `/api/investor/loans/${String(loanId ?? "").trim()}/confirm-sign`,
        method: "POST",
        body: {
          envelopeId: envelopeId ?? null,
          signatureData: String(signatureData ?? ""),
        },
      }),
      invalidatesTags: [
        { type: "InvestorLoans", id: "LIST" },
        { type: "AdminLoans", id: "LIST" },
      ],
    }),
    /** POST /api/investor/loans/apply — submit an asset-backed loan application */
    applyForLoan: builder.mutation({
      query: (body) => ({
        url: "/api/investor/loans/apply",
        method: "POST",
        body: {
          assetId: String(body?.assetId ?? "").trim(),
          amount: Number(body?.amount),
          interestRate: Number(body?.interestRate),
          termMonths: Number(body?.termMonths),
          purpose: String(body?.purpose ?? "").trim(),
          annualIncome: Number(body?.annualIncome),
          employmentStatus: String(body?.employmentStatus ?? "").trim(),
        },
      }),
      invalidatesTags: [{ type: "InvestorLoans", id: "LIST" }],
    }),
    getInvestorDashboard: builder.query({
      query: () => ({
        url: "/api/investor/dashboard",
        method: "GET",
      }),
    }),
    getInvestorPayouts: builder.query({
      query: (params = {}) => {
        const q = {
          page: params.page ?? 1,
          limit: params.limit ?? 10,
        };
        const search = String(params.search ?? "").trim();
        if (search) q.search = search;
        const category = String(params.category ?? "").trim();
        if (category && category !== "All Categories") q.category = category;
        const sortBy = String(params.sortBy ?? "").trim();
        if (sortBy) q.sortBy = sortBy;
        const tab = String(params.tab ?? "").trim();
        if (tab) q.tab = tab;
        return {
          url: "/api/investor/payouts",
          method: "GET",
          params: q,
        };
      },
    }),
    getInvestorProfile: builder.query({
      query: () => ({
        url: "/api/investor/get-profile",
        method: "GET",
      }),
    }),
    connectStripeAccount: builder.mutation({
      query: () => ({
        url: "/api/owner/connect-stripe-account",
        method: "POST",
      }),
    }),
    getStripeStatus: builder.query({
      query: () => ({
        url: "/api/owner/get-stripe-status",
        method: "GET",
      }),
    }),
    createStripePaymentIntent: builder.mutation({
      query: (body) => {
        const payload = {
          assetId: String(body?.assetId ?? "").trim(),
          tokenAmount: String(body?.tokenAmount ?? ""),
        };
        const ownerId = String(body?.ownerId ?? "").trim();
        if (ownerId) payload.ownerId = ownerId;
        return {
          url: "/api/investor/payments/stripe/intent",
          method: "POST",
          body: payload,
        };
      },
      extraOptions: { silentToast: true },
    }),
    investorInvest: builder.mutation({
      query: (body) => {
        const payload = {
          assetId: String(body?.assetId ?? "").trim(),
          tokenAmount: Number(body?.tokenAmount),
          paymentMethod: String(body?.paymentMethod ?? "").trim(),
        };
        const pid = String(body?.paymentIntentId ?? "").trim();
        if (pid) payload.paymentIntentId = pid;
        return {
          url: "/api/investor/invest",
          method: "POST",
          body: payload,
        };
      },
    }),
    getNonce: builder.query({
      query: () => ({
        url: "/api/wallet/link/nonce",
        method: "GET",
      }),
    }),
    verifyAndLink: builder.mutation({
      query: (userData) => ({
        url: "/api/wallet/verify-and-link",
        method: "POST",
        body: userData,
      }),
    }),
    dissconnect: builder.mutation({
      query: (userData) => ({
        url: "/api/wallet/disconnect",
        method: "POST",
        body: userData,
      }),
    }),
    approveAdminAsset: builder.mutation({
      query: (body) => ({
        url: "/api/admin/asset/approve",
        method: "POST",
        body: {
          assetId: String(body?.assetId ?? "").trim(),
        },
      }),
      invalidatesTags: (result, error, arg) => {
        const id = String(arg?.assetId ?? "").trim();
        return [
          { type: "AdminAssets", id: "LIST" },
          ...(id ? [{ type: "AdminAsset", id }] : []),
        ];
      },
    }),
    rejectAdminAsset: builder.mutation({
      query: (body) => ({
        url: "/api/admin/asset/reject",
        method: "POST",
        body: {
          assetId: String(body?.assetId ?? "").trim(),
          rejectionReason: String(body?.rejectionReason ?? ""),
          filesNeedToBeUpdated: Array.isArray(body?.filesNeedToBeUpdated)
            ? body.filesNeedToBeUpdated.map((x) => String(x))
            : [],
        },
      }),
      invalidatesTags: (result, error, arg) => {
        const id = String(arg?.assetId ?? "").trim();
        return [
          { type: "AdminAssets", id: "LIST" },
          ...(id ? [{ type: "AdminAsset", id }] : []),
        ];
      },
    }),
    getUsersForVerification: builder.query({
      query: (params = {}) => {
        const q = {
          role: params.role ?? "investor",
          page: params.page ?? 1,
          limit: params.limit ?? 10,
        };
        if (params.tab) q.tab = params.tab;
        if (params.search != null && String(params.search).trim() !== "") {
          q.search = String(params.search).trim();
        }
        if (params.type != null && params.type !== "" && params.type !== "all") {
          q.type = params.type;
        }
        if (params.status != null && params.status !== "" && params.status !== "all") {
          q.status = params.status;
        }
        return {
          url: "/api/admin/get-users-for-verification",
          method: "GET",
          params: q,
        };
      },
      providesTags: [{ type: "VerificationList", id: "LIST" }],
    }),
    getVerificationDetail: builder.query({
      query: ({ type, id }) => ({
        url: "/api/admin/verification/detail",
        method: "GET",
        params: {
          type: String(type ?? "").trim(),
          id: String(id ?? "").trim(),
        },
      }),
      providesTags: (result, error, arg) => {
        const t = String(arg?.type ?? "").trim();
        const i = String(arg?.id ?? "").trim();
        if (!t || !i) return [];
        return [{ type: "VerificationDetail", id: `${t}:${i}` }];
      },
    }),
    verifyRegaAsset: builder.mutation({
      query: (body) => ({
        url: "/api/admin/verify-reject-rega-asset",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "VerificationDetail" }, { type: "VerificationList", id: "LIST" }],
    }),
    verifyNafathUser: builder.mutation({
      query: (body) => ({
        url: "/api/admin/verify-reject-nafath-user",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "VerificationDetail" }, { type: "VerificationList", id: "LIST" }],
    }),
    verifyTaufeeq: builder.mutation({
      query: (body) => ({
        url: "/api/admin/verify-reject-taufeeq",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "VerificationDetail" }, { type: "VerificationList", id: "LIST" }],
    }),
    verifyVaraDocument: builder.mutation({
      query: (body) => ({
        url: "/api/admin/verify-vara-document",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "VerificationDetail" }, { type: "VerificationList", id: "LIST" }],
    }),
    updateAdminSettings: builder.mutation({
      query: (userData) => ({
        url: "/api/admin/update-platform-settings",
        method: "POST",
        body: userData,
      }),
    }),
    forgotPassword: builder.mutation({
      query: (body) => ({
        url: "/api/auth/forgot-password",
        method: "POST",
        body,
      }),
    }),
    resetPassword: builder.mutation({
      query: (email) => ({
        url: "/api/auth/reset-password",
        method: "POST",
        body: email,
      }),
    }),
    Logout: builder.mutation({
      query: (userData) => ({
        url: "/api/auth/logout",
        method: "POST",
        body: userData,
      }),
    }),
    listning: builder.query({
      query: () => ({
        url: "/api/owner/my-assets",
        method: "GET",
      }),
      providesTags: [{ type: "OwnerListings", id: "LIST" }],
    }),
    /** POST /api/owner/asset/:assetId/mint — owner signs message, submits signature + message */
    mintOwnerAsset: builder.mutation({
      query: ({ assetId, signature, message }) => {
        const id = encodeURIComponent(String(assetId ?? "").trim());
        return {
          url: `/api/owner/asset/${id}/mint`,
          method: "POST",
          body: {
            signature: String(signature ?? "").trim(),
            message: String(message ?? "").trim(),
          },
        };
      },
    }),
    /** POST /api/owner/staking/create — create a staking pool for one of the owner's assets */
    createStakingPool: builder.mutation({
      query: (body) => ({
        url: "/api/owner/staking/create",
        method: "POST",
        body: {
          assetId: String(body?.assetId ?? "").trim(),
          poolName: String(body?.poolName ?? "").trim(),
          annualYield: Number(body?.annualYield),
          lockPeriod: Number(body?.lockPeriod),
          minStake: Number(body?.minStake),
          targetStakeAmount: Number(body?.targetStakeAmount),
        },
      }),
      invalidatesTags: [{ type: "OwnerStakingPools", id: "LIST" }],
    }),
    /** GET /api/owner/staking/my-pools — staking pools created by the current owner */
    getMyStakingPools: builder.query({
      query: () => ({
        url: "/api/owner/staking/my-pools",
        method: "GET",
      }),
      providesTags: [{ type: "OwnerStakingPools", id: "LIST" }],
    }),
    /** GET /api/admin/staking/pools — staking pools listed by asset owners, for admin review */
    getAdminStakingPools: builder.query({
      query: () => ({
        url: "/api/admin/staking/pools",
        method: "GET",
      }),
      providesTags: [{ type: "AdminStakingPools", id: "LIST" }],
    }),
    /** POST /api/admin/staking/approve — approve a pending staking pool */
    approveAdminStakingPool: builder.mutation({
      query: (body) => ({
        url: "/api/admin/staking/approve",
        method: "POST",
        body: {
          poolId: String(body?.poolId ?? "").trim(),
        },
      }),
      invalidatesTags: [{ type: "AdminStakingPools", id: "LIST" }],
    }),
    /** POST /api/admin/staking/reject — reject a pending staking pool */
    rejectAdminStakingPool: builder.mutation({
      query: (body) => ({
        url: "/api/admin/staking/reject",
        method: "POST",
        body: {
          poolId: String(body?.poolId ?? "").trim(),
          rejectionReason: String(body?.rejectionReason ?? ""),
        },
      }),
      invalidatesTags: [{ type: "AdminStakingPools", id: "LIST" }],
    }),
    getProfile: builder.query({
      query: () => ({
        url: "/api/owner/get-profile",
        method: "GET",
      }),
    }),
    getAdminProfile: builder.query({
      query: () => ({
        url: "/api/admin/get-profile",
        method: "GET",
      }),
    }),
    getUpdateProfile: builder.mutation({
      query: (userData) => ({
        url: "/api/owner/update-profile-settings",
        method: "POST",
        body: userData,
      }),
    }),
    getRegistrationData: builder.query({
      query: () => ({
        url: "/api/location/registrationData",
        method: "GET",
      }),
    }),
    VerifyUser: builder.mutation({
      query: (userData) => ({
        url: "/api/admin/verify-user",
        method: "POST",
        body: userData,
      }),
    }),
    getInvestorUserData: builder.query({
      query: (params = {}) => ({
        url: "/api/admin/get-investors",
        method: "GET",
        params: buildAdminUserListQueryParams(params),
      }),
    }),
    getOwnerUserData: builder.query({
      query: (params = {}) => ({
        url: "/api/admin/get-owners",
        method: "GET",
        params: buildAdminUserListQueryParams(params),
      }),
    }),
    /** Admin user detail: profile, summary, investments (paged), documents */
    getAdminInvestorDetails: builder.query({
      query: ({ userId, page = 1, limit = 5 }) => ({
        url: `/api/admin/investors/${encodeURIComponent(String(userId ?? "").trim())}`,
        method: "GET",
        params: {
          page: Number(page) > 0 ? Number(page) : 1,
          limit: Number(limit) > 0 ? Number(limit) : 5,
        },
      }),
    }),
    getAdminOwnerDetails: builder.query({
      query: (userId) => ({
        url: `/api/admin/owners/${encodeURIComponent(String(userId ?? "").trim())}`,
        method: "GET",
      }),
    }),
    ChangeUserStatus: builder.mutation({
      query: (userData) => ({
        url: "api/admin/change-user-status",
        method: "POST",
        body: userData,
      }),
    }),
    CreateAsset: builder.mutation({
      query: (body) => ({
        url: "/api/owner/create-asset",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "OwnerListings", id: "LIST" }],
    }),
    /** PATCH multipart — same shape as create; `/api/owner/assets/:assetId` */
    UpdateOwnerAsset: builder.mutation({
      query: ({ assetId, body }) => {
        const id = encodeURIComponent(String(assetId ?? "").trim());
        return {
          url: `/api/owner/assets/${id}`,
          method: "PUT",
          body,
        };
      },
      invalidatesTags: (result, error, { assetId }) => [
        { type: "OwnerListings", id: "LIST" },
        { type: "OwnerAssetDetail", id: String(assetId ?? "").trim() || "UNKNOWN" },
      ],
    }),
    VaraVerificationDetails: builder.mutation({
      query: (userData) => ({
        url: "api/owner/vara-verification-detail",
        method: "POST",
        body: userData,
      }),
    }),
    NafathDocuments: builder.mutation({
      query: (body) => ({
        url: "/api/nafath/documents",
        method: "POST",
        body,
      }),
    }),
    /** Owner multipart — passport / funds / address files + VARA text fields */
    updateVaraVerificationDocument: builder.mutation({
      query: (body) => ({
        url: "/api/owner/update-vara-verification-document",
        method: "POST",
        body,
      }),
    }),
    AssetCategory: builder.query({
      query: () => ({
        url: "/api/owner/assetCategory",
        method: "GET",
      }),
    }),
    investorAssetCategory: builder.query({
      query: () => ({
        url: "/api/investor/assetCategory",
        method: "GET",
      }),
    }),
    /** GET /api/investor/staking/pools?category=<categoryId> — active staking pools for a category */
    getInvestorStakingPools: builder.query({
      query: (category) => ({
        url: "/api/investor/staking/pools",
        method: "GET",
        params: { category: String(category ?? "").trim() },
      }),
      providesTags: [{ type: "InvestorStakingPools", id: "LIST" }],
    }),
    /** POST /api/investor/staking/stake — stake into a pool */
    stakeInPool: builder.mutation({
      query: (body) => ({
        url: "/api/investor/staking/stake",
        method: "POST",
        body: {
          poolId: String(body?.poolId ?? "").trim(),
          amountInSAR: Number(body?.amountInSAR),
        },
      }),
      invalidatesTags: [
        { type: "InvestorStakingPools", id: "LIST" },
        { type: "InvestorStakingPositions", id: "LIST" },
      ],
    }),
    /** GET /api/investor/staking/my-stakings — the current investor's staking positions */
    getMyStakingPositions: builder.query({
      query: () => ({
        url: "/api/investor/staking/my-stakings",
        method: "GET",
      }),
      providesTags: [{ type: "InvestorStakingPositions", id: "LIST" }],
    }),
    AssetCreationData: builder.query({
      query: () => ({
        url: "/api/location/assetCreationData",
        method: "GET",
      }),
    }),
    Countries: builder.query({
      query: () => ({
        url: "/api/location/countries",
        method: "GET",
      }),
    }),
    States: builder.query({
      query: (countryId) => ({
        url: `/api/location/states/${countryId}`,
        method: "GET",
      }),
    }),
    Cities: builder.query({
      query: (stateId) => ({
        url: `/api/location/cities/${stateId}`,
        method: "GET",
      }),
    }),
    VerifyKYC: builder.mutation({
      query: (body) => ({
        url: "/api/admin/kyc/verify",
        method: "POST",
        body: body,
      }),
    }),
    getSecondaryAssets: builder.query({
      query: () => ({
        url: "/api/secondary/assets",
        method: "GET",
      }),
    }),
    secondaryBuy: builder.mutation({
      query: (body) => ({
        url: "/api/secondary/buy",
        method: "POST",
        body,
      }),
    }),
    secondarySell: builder.mutation({
      query: (body) => ({
        url: "/api/secondary/sell",
        method: "POST",
        body,
      }),
    }),
    checkEmailExists: builder.query({
      query: ({ email, role }) => ({
        url: "/api/auth/check-email-already-exists",
        method: "GET",
        params: { email, role },
      }),
    }),
    getSecondaryMarketOverview: builder.query({
      query: (arg) => {
        const cached =
          arg && typeof arg === "object" && arg !== null && arg.cached === true;
        const qs = cached ? "?cached=true" : "";
        return {
          url: `/api/secondary/market-overview${qs}`,
          method: "GET",
        };
      },
      serializeQueryArgs: () => "getSecondaryMarketOverview",
    }),
    markAllNotificationsRead: builder.mutation({
      query: () => ({
        url: "/api/admin/mark-all-notifications-read",
        method: "POST",
      }),
      extraOptions: { silentToast: true },
    }),
    markAllOwnerNotificationsRead: builder.mutation({
      query: () => ({
        url: "/api/owner/mark-all-notifications-read",
        method: "POST",
      }),
      extraOptions: { silentToast: true },
    }),
    markAllInvestorNotificationsRead: builder.mutation({
      query: () => ({
        url: "/api/investor/mark-all-notifications-read",
        method: "POST",
      }),
      extraOptions: { silentToast: true },
    }),
    getAdminNotifications: builder.query({
      query: (params = {}) => {
        const page = Number(params.page) > 0 ? Number(params.page) : 1;
        const limit = Number(params.limit) > 0 ? Number(params.limit) : 10;
        const cached = params.cached === true;
        return {
          url: "/api/admin/get-notifications",
          method: "GET",
          params: { page, limit, cached },
        };
      },
      // Share the same cache entry for cached=true (polling) and cached=false (manual refresh)
      serializeQueryArgs: ({ queryArgs, endpointName }) => {
        const page = Number(queryArgs?.page) > 0 ? Number(queryArgs.page) : 1;
        const limit = Number(queryArgs?.limit) > 0 ? Number(queryArgs.limit) : 10;
        return `${endpointName}(page:${page},limit:${limit})`;
      },
      extraOptions: { silentToast: true },
    }),
    getOwnerNotifications: builder.query({
      query: (params = {}) => {
        const page = Number(params.page) > 0 ? Number(params.page) : 1;
        const limit = Number(params.limit) > 0 ? Number(params.limit) : 10;
        const cached = params.cached === true;
        return {
          url: "/api/owner/get-notifications",
          method: "GET",
          params: { page, limit, cached },
        };
      },
      serializeQueryArgs: ({ queryArgs, endpointName }) => {
        const page = Number(queryArgs?.page) > 0 ? Number(queryArgs.page) : 1;
        const limit = Number(queryArgs?.limit) > 0 ? Number(queryArgs.limit) : 10;
        return `${endpointName}(page:${page},limit:${limit})`;
      },
      extraOptions: { silentToast: true },
    }),
    getInvestorNotifications: builder.query({
      query: (params = {}) => {
        const page = Number(params.page) > 0 ? Number(params.page) : 1;
        const limit = Number(params.limit) > 0 ? Number(params.limit) : 10;
        const cached = params.cached === true;
        return {
          url: "/api/investor/get-notifications",
          method: "GET",
          params: { page, limit, cached },
        };
      },
      serializeQueryArgs: ({ queryArgs, endpointName }) => {
        const page = Number(queryArgs?.page) > 0 ? Number(queryArgs.page) : 1;
        const limit = Number(queryArgs?.limit) > 0 ? Number(queryArgs.limit) : 10;
        return `${endpointName}(page:${page},limit:${limit})`;
      },
      extraOptions: { silentToast: true },
    }),
    getSecondaryAssetDetail: builder.query({
      query: (arg) => {
        const assetId =
          arg && typeof arg === "object" && "assetId" in arg ? arg.assetId : arg;
        const cached =
          arg && typeof arg === "object" && arg !== null && arg.cached === true;
        const id = encodeURIComponent(String(assetId ?? "").trim());
        const qs = cached ? "?cached=true" : "";
        return {
          url: `/api/secondary/asset/${id}${qs}`,
          method: "GET",
        };
      },
      // Same cache entry whether the request used ?cached=true or not (only URL differs).
      serializeQueryArgs: ({ queryArgs, endpointName }) => {
        const id =
          queryArgs && typeof queryArgs === "object" && "assetId" in queryArgs
            ? queryArgs.assetId
            : queryArgs;
        return `${endpointName}(${String(id ?? "")})`;
      },
    }),
  }),
});

export const {
  useLoginMutation,
  useSignupMutation,
  useGetAdminSettingsQuery,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useLogoutMutation,
  useListningQuery,
  useMintOwnerAssetMutation,
  useCreateStakingPoolMutation,
  useGetMyStakingPoolsQuery,
  useGetAdminStakingPoolsQuery,
  useApproveAdminStakingPoolMutation,
  useRejectAdminStakingPoolMutation,
  useGetProfileQuery,
  useGetAdminProfileQuery,
  useGetUpdateProfileMutation,
  useGetRegistrationDataQuery,
  useGetAdminLogsQuery,
  useGetAdminDashboardQuery,
  useGetAdminAssetsQuery,
  useGetAdminAssetDetailQuery,
  useGetInvestorAssetsQuery,
  useGetInvestorAssetByIdQuery,
  useLazyGetInvestorAssetByIdQuery,
  useGetOwnerAssetByIdQuery,
  useGetOwnerAssetInvestorsQuery,
  useGetInvestorAssetSummaryQuery,
  useGetMyInvestmentsQuery,
  useGetAdminLoansQuery,
  useReviewAdminLoanMutation,
  useSignOffAdminLoanMutation,
  useGetInvestorLoansQuery,
  useGetLoanCollateralPreviewQuery,
  useApplyForLoanMutation,
  useInitiateLoanSignMutation,
  useConfirmLoanSignMutation,
  useGetInvestorDashboardQuery,
  useGetInvestorPayoutsQuery,
  useGetInvestorProfileQuery,
  useConnectStripeAccountMutation,
  useGetStripeStatusQuery,
  useCreateStripePaymentIntentMutation,
  useInvestorInvestMutation,
  useGetNonceQuery,
  useVerifyAndLinkMutation,
  useDissconnectMutation,
  useApproveAdminAssetMutation,
  useRejectAdminAssetMutation,
  useGetUsersForVerificationQuery,
  useGetVerificationDetailQuery,
  useVerifyRegaAssetMutation,
  useVerifyNafathUserMutation,
  useVerifyTaufeeqMutation,
  useVerifyVaraDocumentMutation,
  useUpdateAdminSettingsMutation,
  useVerifyUserMutation,
  useGetInvestorUserDataQuery,
  useGetOwnerUserDataQuery,
  useGetAdminInvestorDetailsQuery,
  useGetAdminOwnerDetailsQuery,
  useChangeUserStatusMutation,
  useCreateAssetMutation,
  useUpdateOwnerAssetMutation,
  useVaraVerificationDetailsMutation,
  useNafathDocumentsMutation,
  useUpdateVaraVerificationDocumentMutation,
  useAssetCategoryQuery,
  useInvestorAssetCategoryQuery,
  useGetInvestorStakingPoolsQuery,
  useStakeInPoolMutation,
  useGetMyStakingPositionsQuery,
  useAssetCreationDataQuery,
  useCountriesQuery,
  useCitiesQuery,
  useStatesQuery,
  useVerifyKYCMutation,
  useGetSecondaryAssetsQuery,
  useGetSecondaryAssetDetailQuery,
  useGetSecondaryMarketOverviewQuery,
  useSecondaryBuyMutation,
  useSecondarySellMutation,
  useLazyCheckEmailExistsQuery,
  useGetAdminNotificationsQuery,
  useLazyGetAdminNotificationsQuery,
  useMarkAllNotificationsReadMutation,
  useGetOwnerNotificationsQuery,
  useLazyGetOwnerNotificationsQuery,
  useMarkAllOwnerNotificationsReadMutation,
  useGetInvestorNotificationsQuery,
  useLazyGetInvestorNotificationsQuery,
  useMarkAllInvestorNotificationsReadMutation,
} = authApi;
